import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2, Star, Camera } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ParentSurveyPage() {
  const [config, setConfig] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/parent-survey/config`)
      .then(res => setConfig(res.data))
      .catch(() => setError("Could not load the survey. Please try again later."))
      .finally(() => setLoading(false));
  }, []);

  const setAnswer = (id, value) => setAnswers(prev => ({ ...prev, [id]: value }));

  const handleSubmit = async () => {
    const qs = config?.questions || [];
    for (const q of qs) {
      if (q.required && q.type !== "info" && !String(answers[q.id] || "").trim()) {
        setError(`Please answer: "${q.label}"`);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
    setError("");
    setSubmitting(true);
    try {
      const payload = qs.filter(q => q.type !== "info").map(q => ({
        id: q.id, label: q.label, answer: answers[q.id] || "",
      }));
      await axios.post(`${API}/parent-survey/submit`, { answers: payload });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  const ty = config?.thank_you || {};

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-cyan-200 shadow-xl" data-testid="survey-thankyou">
          <CardContent className="p-8 text-center space-y-5">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-bold text-[#1E3A8A]">{ty.heading || "Thank you!"}</h1>
            <p className="text-sm text-gray-600">{ty.message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {ty.google_url && (
                <a href={ty.google_url} target="_blank" rel="noreferrer" data-testid="survey-google-link"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#1E3A8A] text-white font-bold text-sm hover:bg-blue-800 transition-colors">
                  <Star className="w-4 h-4" /> Review us on Google
                </a>
              )}
              {ty.yelp_url && (
                <a href={ty.yelp_url} target="_blank" rel="noreferrer" data-testid="survey-yelp-link"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 transition-colors">
                  <Star className="w-4 h-4" /> Review us on Yelp
                </a>
              )}
            </div>
            {ty.screenshot_note && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                <Camera className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800 font-medium" data-testid="survey-screenshot-note">{ty.screenshot_note}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white" data-testid="parent-survey-page">
      <header className="bg-[#1E3A8A] text-white py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl sm:text-2xl font-bold">{config?.title || "Parent Survey"}</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto p-4 space-y-4 pb-16">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 font-medium" data-testid="survey-error">{error}</div>
        )}
        <Card className="border-cyan-100">
          <CardContent className="p-4">
            <p className="text-sm text-gray-700 whitespace-pre-line">{config?.intro}</p>
          </CardContent>
        </Card>

        {(config?.questions || []).map((q, idx) => {
          if (q.type === "info") {
            return (
              <Card key={q.id} className="border-blue-100 bg-blue-50/50">
                <CardContent className="p-4">
                  <p className="text-sm font-bold text-[#1E3A8A]">{q.label}</p>
                  {q.help && <p className="text-sm text-gray-700 whitespace-pre-line mt-1">{q.help}</p>}
                </CardContent>
              </Card>
            );
          }
          return (
            <Card key={q.id} className="border-gray-200">
              <CardContent className="p-4 space-y-2">
                <p className="text-sm font-semibold text-gray-900">
                  {q.label} {q.required && <span className="text-red-500">*</span>}
                </p>
                {q.help && <p className="text-xs text-gray-500 italic">{q.help}</p>}
                {q.type === "select" ? (
                  <div className="space-y-1.5" data-testid={`survey-q-${q.id}`}>
                    {(q.options || []).map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setAnswer(q.id, opt)}
                        className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
                          answers[q.id] === opt
                            ? "border-[#1E3A8A] bg-blue-50 text-[#1E3A8A] font-semibold"
                            : "border-gray-200 hover:border-blue-300 text-gray-700"
                        }`}
                        data-testid={`survey-q-${q.id}-opt-${(q.options || []).indexOf(opt)}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : (
                  <Textarea
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    rows={3}
                    placeholder="Your answer…"
                    data-testid={`survey-q-${q.id}`}
                  />
                )}
              </CardContent>
            </Card>
          );
        })}

        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-[#1E3A8A] hover:bg-blue-800 text-white font-bold py-6 text-base rounded-xl"
          data-testid="survey-submit-btn"
        >
          {submitting ? "Submitting…" : "Submit Survey"}
        </Button>
      </main>
    </div>
  );
}
